import type {PHPW} from "../../generated";
import {ApiService} from "./ApiService";

export class SetupService extends ApiService {
    public getDeviceType = async (abortSignal?: AbortSignal) => {
        const result = await this.apiCaller.call<PHPW.Api.Routes.Lib.Setup.Model.DeviceType>({
            route: ["setup", "deviceType"],
            method: "GET",
            abortSignal: abortSignal
        });

        return typeof result.response === "string" ? undefined : result.response;
    };

    public getBookingGesellschafterById = async (args: PHPW.Api.Lib.Common.ArgumentsId, abortSignal?: AbortSignal) => {
        const result = await this.apiCaller.call<PHPW.Booking.Model.BookingGesellschafter>({
            route: ["setup", "bookingGesellschafter", args.id],
            method: "GET",
            abortSignal: abortSignal
        });

        return typeof result.response === "string" ? undefined : result.response;
    };

    public postBookingGesellschafter = async (
        payload: PHPW.Booking.Model.BookingGesellschafter,
        abortSignal?: AbortSignal
    ) => {
        const result = await this.apiCaller.call<PHPW.Api.Responses.SaveResponse>({
            route: ["setup", "bookingGesellschafter"],
            method: "POST",
            abortSignal: abortSignal,
            body: payload
        });

        return typeof result.response === "string" ? undefined : result.response;
    };

    public getMailAccount = async (abortSignal?: AbortSignal) => {
        const result = await this.apiCaller.call<PHPW.Api.Routes.Lib.Setup.Model.MailAccount[]>({
            route: ["setup", "mailAccount"],
            method: "GET",
            abortSignal: abortSignal
        });

        return typeof result.response === "string" ? undefined : result.response;
    };

    public getMailAccountById = async (args: PHPW.Api.Lib.Common.ArgumentsId, abortSignal?: AbortSignal) => {
        const result = await this.apiCaller.call<PHPW.Api.Routes.Lib.Setup.Model.MailAccount>({
            route: ["setup", "mailAccount", args.id],
            method: "GET",
            abortSignal: abortSignal
        });

        return typeof result.response === "string" ? undefined : result.response;
    };

    public postMailAccount = async (
        payload: PHPW.Api.Routes.Lib.Setup.Model.MailAccount,
        abortSignal?: AbortSignal
    ) => {
        const result = await this.apiCaller.call<PHPW.Api.Responses.SaveResponse>({
            route: ["setup", "mailAccount"],
            method: "POST",
            abortSignal: abortSignal,
            body: payload
        });

        return typeof result.response === "string" ? undefined : result.response;
    };

    public deleteMailAccountById = async (args: PHPW.Api.Lib.Common.ArgumentsId, abortSignal?: AbortSignal) => {
        const result = await this.apiCaller.call<PHPW.Api.Responses.DeleteResponse>({
            route: ["setup", "mailAccount", args.id],
            method: "DELETE",
            abortSignal: abortSignal
        });

        return typeof result.response === "string" ? undefined : result.response;
    };

    public postMailAccountImapActionTest = async (
        payload: PHPW.Api.Routes.Lib.Setup.Model.MailAccount,
        abortSignal?: AbortSignal
    ) => {
        const result = await this.apiCaller.call<PHPW.Mail.Model.TestImapResult>({
            route: ["setup", "mailAccount", "imap", "action", "test"],
            method: "POST",
            abortSignal: abortSignal,
            body: payload
        });

        return typeof result.response === "string" ? undefined : result.response;
    };

    public postMailAccountSmtpActionTest = async (
        payload: PHPW.Api.Routes.Lib.Setup.Model.MailAccount,
        abortSignal?: AbortSignal
    ) => {
        const result = await this.apiCaller.call<PHPW.Mail.Model.TestSmtpResult>({
            route: ["setup", "mailAccount", "smtp", "action", "test"],
            method: "POST",
            abortSignal: abortSignal,
            body: payload
        });

        return typeof result.response === "string" ? undefined : result.response;
    };

    public getImapMailboxById = async (args: PHPW.Api.Lib.Common.ArgumentsId, abortSignal?: AbortSignal) => {
        const result = await this.apiCaller.call<PHPW.Api.Routes.Lib.Setup.Model.ImapMailbox>({
            route: ["setup", "imapMailbox", args.id],
            method: "GET",
            abortSignal: abortSignal
        });

        return typeof result.response === "string" ? undefined : result.response;
    };

    public postImapMailbox = async (
        payload: PHPW.Api.Routes.Lib.Setup.Model.ImapMailbox,
        abortSignal?: AbortSignal
    ) => {
        const result = await this.apiCaller.call<PHPW.Api.Responses.SaveResponse>({
            route: ["setup", "imapMailbox"],
            method: "POST",
            abortSignal: abortSignal,
            body: payload
        });

        return typeof result.response === "string" ? undefined : result.response;
    };

    public getPaymentById = async (args: PHPW.Api.Lib.Common.ArgumentsId, abortSignal?: AbortSignal) => {
        const result = await this.apiCaller.call<PHPW.Api.Routes.Lib.Setup.Model.Payment>({
            route: ["setup", "payment", args.id],
            method: "GET",
            abortSignal: abortSignal
        });

        return typeof result.response === "string" ? undefined : result.response;
    };

    public postPayment = async (payload: PHPW.Api.Routes.Lib.Setup.Model.Payment, abortSignal?: AbortSignal) => {
        const result = await this.apiCaller.call<PHPW.Api.Responses.SaveResponse>({
            route: ["setup", "payment"],
            method: "POST",
            abortSignal: abortSignal,
            body: payload
        });

        return typeof result.response === "string" ? undefined : result.response;
    };

    public deleteShopById = async (
        args: PHPW.Api.Lib.Common.ArgumentsId,
        params: PHPW.Api.Lib.Common.ParamsDelete,
        abortSignal?: AbortSignal
    ) => {
        const result = await this.apiCaller.call<PHPW.Api.Responses.DeleteResponse>({
            route: ["setup", "shop", args.id],
            method: "DELETE",
            abortSignal: abortSignal,
            params: params
        });

        return typeof result.response === "string" ? undefined : result.response;
    };

    public getLog = async (abortSignal?: AbortSignal) => {
        const result = await this.apiCaller.call<string>({
            route: ["setup", "log"],
            method: "GET",
            abortSignal: abortSignal
        });

        return typeof result.response === "string" ? result.response : undefined;
    };

    public getBankById = async (args: PHPW.Api.Lib.Common.ArgumentsId, abortSignal?: AbortSignal) => {
        const result = await this.apiCaller.call<PHPW.Api.Routes.Lib.Setup.Model.Bank>({
            route: ["setup", "bank", args.id],
            method: "GET",
            abortSignal: abortSignal
        });

        return typeof result.response === "string" ? undefined : result.response;
    };

    public postBank = async (payload: PHPW.Api.Routes.Lib.Setup.Model.Bank, abortSignal?: AbortSignal) => {
        const result = await this.apiCaller.call<PHPW.Api.Responses.SaveResponse>({
            route: ["setup", "bank"],
            method: "POST",
            abortSignal: abortSignal,
            body: payload
        });

        return typeof result.response === "string" ? undefined : result.response;
    };

    public deleteBankById = async (
        args: PHPW.Api.Lib.Common.ArgumentsId,
        params: PHPW.Api.Lib.Common.ParamsDelete,
        abortSignal?: AbortSignal
    ) => {
        const result = await this.apiCaller.call<PHPW.Api.Responses.DeleteResponse>({
            route: ["setup", "bank", args.id],
            method: "DELETE",
            abortSignal: abortSignal,
            params: params
        });

        return typeof result.response === "string" ? undefined : result.response;
    };
}
